import {Trans} from '@ui/i18n/trans';
import {FileUploadProvider} from '@common/uploads/uploader/file-upload-provider';
import {FormTextField} from '@ui/forms/input-field/text-field/text-field';
import {FormSwitch} from '@ui/forms/toggle/switch';
import {FormSelect, Option} from '@ui/forms/select/select';
import {useForm, useFormContext} from 'react-hook-form';
import {Title} from '@app/titles/models/title';
import {Form} from '@ui/forms/form';
import {toast} from '@ui/toast/toast';
import {message} from '@ui/i18n/message';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import {useOutletContext, useParams} from 'react-router-dom';
import React, {Fragment, useEffect, useState} from 'react';
import {Button} from '@ui/buttons/button';
import {LienEditorLayout} from '@app/admin/liens/lien-editor/lien-editor-layout';
import {
  UpdateLienPayload,
  useUpdateLien,
} from '@app/admin/liens/requests/use-update-link';
import {SelectLanguage} from '@app/admin/liens/lien-editor/select-language';
import {useNormalizedModels} from '@common/ui/normalized-model/use-normalized-models';
import {
  CreateLienPayload,
  useCreateLien,
} from '@app/admin/liens/requests/use-create-lien';
import {useLien} from '@app/liens/requests/use-lien';
import {Lien} from '@app/titles/models/lien';
import {Skeleton} from '@ui/skeleton/skeleton';
import {queryClient} from '@common/http/query-client';
import {DatatableDataQueryKey} from '@common/datatable/requests/paginated-resources';
import {Radio} from '@ui/forms/radio-group/radio';
import {RadioGroup} from '@ui/forms/radio-group/radio-group';
import {FormFileEntryField} from '@common/uploads/components/file-entry-field';
import {Disk} from '@common/uploads/uploader/backend-metadata';
import {Torrent} from '@app/titles/models/torrent';
import {Nzb} from '@app/titles/models/nzb';
import {SectionHelper} from '@common/ui/other/section-helper';
import {ErrorIcon} from '@ui/icons/material/Error';
import {setInLocalStorage} from '@ui/utils/hooks/local-storage';
import {ArticleBodyEditorMenubar} from '@common/article-editor/article-body-editor-menubar';
import ArticleBodyEditor from '@common/article-editor/article-body-editor';
console.log('LienPrimaryFactsForm');
export function LienPrimaryFactsForm() {
  const data = useOutletContext<any>();
  const title = data.title as Title;
  const {lienId, nzbId, torrentId} = useParams();

  return (
    <FileUploadProvider>
      {lienId || torrentId || nzbId ? (
        <EditLienForm title={title} />
      ) : (
        <CreateLinkForm title={title} />
      )}
    </FileUploadProvider>
  );
}

function CreateLinkForm({title}: {title: Title}) {
  const [fullSaison, setFullSaison] = useState(0);
  const navigate = useNavigate();
  const form = useForm<CreateLienPayload>({
    defaultValues: {
      title_id: title.id,
      typefile: 'DDL',
      start_epi: 1,
      saison: 1,
      full_saison: 0,
    },
  });
  const createLien = useCreateLien(form);
  const isDirty = Object.keys(form.formState.dirtyFields).length > 0;
  return (
    <ArticleBodyEditor>
      {(content, editor) => (
        <Form
          form={form}
          onSubmit={values => {
            values['nfo'] = editor.getHTML();
            values['full_saison'] = fullSaison;
            createLien.mutate(values, {
              onSuccess: response => {
                toast(message('Links added'));
                navigate(`/admin/liens/${title.id}`, {
                  relative: 'path',
                  replace: true,
                });
              },
            });
          }}
        >
          <LienEditorLayout
            actions={
              <Button
                variant="flat"
                color="primary"
                type="submit"
                disabled={createLien.isPending || !isDirty}
              >
                <Trans message="Create" />
              </Button>
            }
          >
            <FormFields
              fullSaison={fullSaison}
              setFullSaison={setFullSaison}
              editor={editor}
              content={content}
              clearErrors={form.clearErrors}
              create={true}
              title={title}
            />
          </LienEditorLayout>
        </Form>
      )}
    </ArticleBodyEditor>
  );
}

interface EditLienFormProps {
  title: Title;
}

function EditLienForm({title}: EditLienFormProps) {
  const navigate = useNavigate();
  const {data, isLoading} = useLien('link');

  const lien = data?.model;
  const form = useForm<UpdateLienPayload>({
    defaultValues: {
      qualite: 0,
      saison: 1,
      episode: 1,
      lien: '',
      nfo: '',
      full_saison: 0,
    },
  });

  useEffect(() => {
    if (lien) {
      form.reset({
        langues: lien.langues_compact.map(langue => langue.name),
        subs: lien.subs_compact.map(sub => sub.name),
        qualite: lien.qualite || 0,
        saison: lien.saison,
        episode: lien.episode,
        lien: lien.lien,
        id: lien.id,
        nfo: Array.isArray(lien.nfo)
          ? lien.nfo.map(nfo => nfo.nfo).join('\n')
          : lien.nfo,
        full_saison: lien.full_saison,
      });
    }
  }, [lien, form]);
  const [fullSaison, setFullSaison] = useState(lien?.full_saison || 0);
  const updateLien = useUpdateLien();
  const nfo = Array.isArray(lien?.nfo)
    ? lien.nfo.map(nfo => nfo.nfo).join('\n')
    : lien?.nfo;
  if (isLoading) return <Skeleton className="mb-24" />;
  return (
    <ArticleBodyEditor initialContent={nfo}>
      {(content, editor) => (
        <Form
          form={form}
          onSubmit={values => {
            values['nfo'] = editor.getHTML();
            values['full_saison'] = fullSaison;
            updateLien.mutate(values, {
              onSuccess: async () => {
                queryClient.invalidateQueries({
                  queryKey: DatatableDataQueryKey('liens', {
                    title_id: title.id,
                    loader: 'liens',
                  }),
                });
                await queryClient.invalidateQueries({
                  queryKey: ['liens', values.id],
                });
                navigate(`./../../`, {
                  relative: 'path',
                  replace: true,
                });
              },
            });
          }}
        >
          <LienEditorLayout
            actions={
              <Button
                variant="flat"
                color="primary"
                type="submit"
                disabled={updateLien.isPending}
              >
                <Trans message="Save" />
              </Button>
            }
          >
            <FormFields
              fullSaison={fullSaison}
              setFullSaison={setFullSaison}
              editor={editor}
              content={content}
              clearErrors={form.clearErrors}
              datas={lien}
              create={false}
              title={title}
            />
          </LienEditorLayout>
        </Form>
      )}
    </ArticleBodyEditor>
  );
}

function FormFields({
  title,
  create,
  datas,
  clearErrors,
  register,
  editor,
  content,
  fullSaison,
  setFullSaison,
}: {
  title: Title;
  create?: boolean;
  datas?: Lien | Nzb | Torrent;
  clearErrors: () => void;
  register?: any;
  editor?: any;
  content?: any;
  fullSaison: number;
  setFullSaison: (value: number) => void;
}) {
  const {lienId, nzbId, torrentId} = useParams();
  const data = useOutletContext<any>();
  const passkey = data.passkey;
  const [type, setType] = useState(
    torrentId ? 'TORRENT' : nzbId ? 'NZB' : 'DDL',
  );
  const {setValue} = useFormContext<CreateLienPayload>();
  if (!create && !datas) return <Skeleton className="mb-24" />;

  return (
    <Fragment>
      <input name="title_id" type="hidden" value={title.id} />
      <div className="mb-24 items-center gap-24 md:flex">
        <SelectLanguage
          required={true}
          datas={datas?.langues_compact?.map(langue => ({
            id: langue.id,
            name: langue.name,
          }))}
          type={'langues'}
          className="flex-1 "
          label={'Languages'}
        />
        <SelectLanguage
          type={'subs'}
          datas={datas?.subs_compact?.map(sub => ({
            id: sub.id,
            name: sub.name,
          }))}
          endpoint={'normalized-models/sub'}
          className="flex-1 "
          label={'Subtitles'}
        />
        <QualitySelect selectedValue={datas?.qualite} />
      </div>
      <div className="flex justify-between">
        {title.is_series && (
          <FormSwitch
            value={fullSaison}
            checked={fullSaison === 1}
            onChange={e => {
              const newValue = e.target.checked ? 1 : 0;
              setFullSaison(newValue);
              setValue('full_saison', newValue);
            }}
            name="full_saison"
            className="mb-24"
          >
            <Trans message="One link full season" />
          </FormSwitch>
        )}
      </div>
      <div className="mb-24 items-center gap-24 md:flex">
        {title.is_series && (
          <FormTextField
            name="saison"
            label={<Trans message="Season number" />}
            type="number"
            min={0}
            className="flex-1 max-md:mb-24"
          />
        )}
        {!fullSaison && title.is_series && type !== 'DDL' && (
          <SelectLanguage
            type={'episodes'}
            datas={datas?.episodes}
            endpoint={'normalized-models/episodes'}
            className="flex-1 "
            label={'Episodes'}
          />
        )}
        {!fullSaison && title.is_series && type === 'DDL' && create && (
          <FormTextField
            name="start_epi"
            label={<Trans message="Start Episode number" />}
            type="number"
            min={0}
            className="flex-1 max-md:mb-24"
          />
        )}{' '}
        {!fullSaison && title.is_series && type === 'DDL' && !create && (
          <FormTextField
            name="episode"
            label={<Trans message="Episode number" />}
            type="number"
            min={0}
            className="flex-1 max-md:mb-24"
          />
        )}
      </div>
      {create && (
        <RadioGroup size="sm" className="mb-8" name="type">
          <Radio
            key={1}
            value="DDL"
            checked={type === 'DDL'}
            onChange={e => {
              const newType = e.target.value;
              setType(newType);
              setValue('typefile', newType);
            }}
          >
            <Trans message="Direct link" />
          </Radio>
          <Radio
            key={2}
            value="NZB"
            checked={type === 'NZB'}
            onChange={e => {
              const newType = e.target.value;
              setType(newType);
              setValue('typefile', newType);
            }}
          >
            <Trans message=".nzb (NewsGroup)" />
          </Radio>
          {/*    <Radio
            key={3}
            value="TORRENT"
            checked={type === 'TORRENT'}
            onChange={e => {
              const newType = e.target.value;
              setType(newType);
              setValue('typefile', newType);
            }}
          >
            <Trans message=".torrent" />
          </Radio>*/}
        </RadioGroup>
      )}
      {type === 'TORRENT' && create && (
        <SectionHelper
          leadingIcon={<ErrorIcon size="xs" className="text-primary" />}
          onClose={() => {
            setInLocalStorage('admin-setup-alert-dismissed', {
              timestamp: Date.now(),
            });
          }}
          title={'Torrent'}
          description={
            <Trans
              message={
                'Please use the announce URL above when creating a new torrent : https://darki.zone/announce/:passkey'
              }
              values={{passkey: passkey}}
            />
          }
          color="neutral"
        />
      )}
      {(type === 'NZB' || type === 'TORRENT') && create ? (
        <FormFileEntryField
          required={true}
          name="url"
          diskPrefix="files"
          className="my-24"
          showRemoveButton={true}
          disk={Disk.public}
          allowedFileTypes={type === 'NZB' ? ['.nzb'] : ['.torrent']}
          maxFileSize={1024 * 1024 * 10}
          onChange={clearErrors}
          label={<Trans message="Upload file" />}
        />
      ) : null}
      {type === 'NZB' && (
        <FormTextField
          name="password"
          label={<Trans message="Archive password (optional)" />}
          className="my-24"
        />
      )}
      {type === 'DDL' ? (
        create ? (
          <div className="my-24">
            <FormTextField
              name="lien"
              label={<Trans message="Link" />}
              inputElementType="textarea"
              rows={4}
            />
            <span className="small muted text-xs opacity-50">
              {title.is_series ? (
                <div>
                  <Trans message={'You can use two methods to Post:'} />
                  <br />
                  <span className={'text-xl'}>
                    <Trans message={'Method'} /> 1
                  </span>
                  <br />
                  http://darkibox.com/link
                  <span className={'text-danger'}>::1</span>...
                  <br />
                  http://1fichier.com/link
                  <span className={'text-danger'}>::55</span>...
                  <br />
                  <Trans
                    message={
                      'Replace the number after :: with the episode number.'
                    }
                  />
                  <br />
                  <span className={'text-xl'}>
                    <Trans message={'Method'} /> 2
                  </span>
                  <br />
                  http://darkibox.com/episode1...
                  <br />
                  http://darkibox.com/episode2...
                  <br />
                  http://1fichier.com/episode1
                  <br />
                  http://turbobit.net/episode1
                  <br />
                  <Trans message={'Fill in the field "Start Episode number"'} />
                </div>
              ) : (
                <Trans
                  message={
                    'One link per line, if there are multiple links from the same host, it will be considered multiple parts.'
                  }
                />
              )}
            </span>
          </div>
        ) : (
          <FormTextField
            name="lien"
            label={<Trans message="Link" />}
            className="mb-24"
          />
        )
      ) : null}
      <div>
        .nfo
        <ArticleBodyEditorMenubar
          hideInsertButton={true}
          hideImageButton={true}
          editor={editor}
          size="sm"
        />
        <div className="w-full">{content}</div>
      </div>
    </Fragment>
  );
}

function QualitySelect({selectedValue}: {selectedValue?: number}) {
  const [query, setQuery] = useState('');
  const {data} = useNormalizedModels('normalized-models/quality', {
    query,
  });

  return (
    <FormSelect
      name="qualite"
      defaultValue={selectedValue}
      selectionMode="single"
      label={<Trans message="Quality" />}
      showSearchField
      searchPlaceholder="Search quality"
      className="flex-1"
    >
      {data?.results
        .filter(suggestion => suggestion.name != 'MULTI')
        .map(language => (
          <Option key={language.id} value={language.id}>
            {language.name}
          </Option>
        ))}
    </FormSelect>
  );
}
